import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { B2BAllowedAuthMethods } from '@stytch/react/b2b';
import { useParams } from 'react-router';

import { useOrganizationBySlug } from '@/api/organization';
import ForgotPasswordForm from '@/app/sign-in/ForgotPasswordForm';
import SignInPanel from '@/app/sign-in/SignInPanel';
import { PageSpinner } from '@/components/loaders';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import useDeferredLoading from '@/hooks/useDeferredLoading';
import { ROUTES } from '@/router';

import InstanceError from '../InstanceError';
import TenantAuthenticate from '../TenantAuthenticate';

const InstanceForgotPassword = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { organization, isLoading } = useOrganizationBySlug(orgSlug);
  const showLoading = useDeferredLoading(isLoading);

  const instanceSignInPath = ROUTES.signInInst.path.replace(':orgSlug', orgSlug ?? '');
  const passwordSignInPath = ROUTES.signInInstPassword.path.replace(':orgSlug', orgSlug ?? '');

  if (!orgSlug) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={<Trans>No instance specified.</Trans>}
      />
    );
  }

  if (isLoading && showLoading) {
    return <PageSpinner />;
  }

  if (isLoading) {
    return null;
  }

  if (!organization) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={
          <Trans>The instance "{orgSlug}" was not found or you don't have access to it.</Trans>
        }
      />
    );
  }

  // Check if password is allowed for this organization
  const allowedMethods: B2BAllowedAuthMethods[] = organization.allowed_auth_methods || [];
  const authMethodsMode = organization.auth_methods;
  const isPasswordAllowed =
    authMethodsMode === 'ALL_ALLOWED' || allowedMethods.includes('password');

  if (!isPasswordAllowed) {
    return (
      <InstanceError
        title={t`Password Reset Not Available`}
        message={
          <Trans>
            Password sign-in is not enabled for {organization.organization_name}. Please use another
            sign-in method.
          </Trans>
        }
      />
    );
  }

  return (
    <FullPage title={t`Reset Password`}>
      <TenantAuthenticate orgSlug={orgSlug}>
        <SignInPanel
          title={t`Reset your password`}
          description={
            <Trans>
              We can email you a one-time password reset link for {organization.organization_name}.
            </Trans>
          }
          footer={
            <>
              <Text variant="caption1" color="grey">
                <Trans>
                  Don't want to reset your password?{' '}
                  <Link variant="caption1" color="aqua" to={passwordSignInPath}>
                    {t`Sign in with password`}
                  </Link>
                </Trans>
              </Text>
              <Text variant="caption1" color="grey">
                <Trans>
                  Prefer to not use a password?{' '}
                  <Link variant="caption1" color="aqua" to={instanceSignInPath}>
                    Sign in another way
                  </Link>
                </Trans>
              </Text>
            </>
          }
        >
          <ForgotPasswordForm organizationId={organization.organization_id} />
        </SignInPanel>
      </TenantAuthenticate>
    </FullPage>
  );
};

export default InstanceForgotPassword;
